<template>
    <div id="post-wizard-tour-event-status" class="is-flex is-align-items-center is-flex-wrap-wrap" style="gap: 0.5rem">
        <!-- STATUS - Plain coloured text, not a filled tag, so it cannot be mistaken for a button -->
        <span class="is-flex is-align-items-center has-text-weight-semibold mr-2" :class="statusTextClass">
            <span class="icon">
                <i class="fas" :class="statusIcon"></i>
            </span>
            <span>{{ statusText }}</span>
        </span>

        <!-- ACTION - Primary call to action while the event is an unpublished Draft, subtle afterwards -->
        <button
            class="button is-small"
            :class="{ 'is-success': isPublishCta }"
            @click="openStatusModal"
            :disabled="isUnderReview || !canChangeStatus"
        >
            <span v-if="isPublishCta" class="icon">
                <i class="fas fa-rocket"></i>
            </span>
            <span>{{ statusButtonText }}</span>
            <span v-if="!isPublishCta" class="icon">
                <i class="fas fa-chevron-down"></i>
            </span>
        </button>
    </div>
</template>

<script setup lang="ts">
    import { computed } from "vue";
    import { translate } from "../../Common/translations";
    import { useEditEventStore } from "../editEventStore";
    import { EVENT_STATUS, EventStatus, EVENT_VISIBILITY, Visibility } from "../../Common/types";
    import { eventBus, EVENT_STATUS_OPEN } from "../eventBus";

    const store = useEditEventStore();

    const currentStatus = computed<EventStatus>(() => store.event.status);
    const currentVisibility = computed<Visibility>(() => store.event.visibility);
    const canChangeStatus = computed(() => store.metadata?.canChangeStatus ?? true);
    // Review is now a federation-level state, resolved by the backend from the federation's
    // approval request — not an event status.
    const isUnderReview = computed(() => store.metadata?.isUnderReview ?? false);
    const isPrivate = computed(() => currentVisibility.value === EVENT_VISIBILITY.Private);

    const isPublishCta = computed(() => currentStatus.value === EVENT_STATUS.Draft && !isUnderReview.value && canChangeStatus.value);

    const statusIcon = computed(() => {
        if (isUnderReview.value) {
            return "fa-clock";
        }
        switch (currentStatus.value) {
            case EVENT_STATUS.Draft:
                return "fa-file-alt";
            case EVENT_STATUS.RegistrationBySchedule:
                return isPrivate.value ? "fa-link" : "fa-globe";
            case EVENT_STATUS.RegistrationOpen:
                return isPrivate.value ? "fa-link" : "fa-check-circle";
            case EVENT_STATUS.Archived:
                return "fa-box";
            default:
                return "fa-file-alt";
        }
    });

    const statusText = computed(() => {
        if (isUnderReview.value) {
            return translate("event_status.indicator.pending_review");
        }
        switch (currentStatus.value) {
            case EVENT_STATUS.Draft:
                return translate("event_status.indicator.draft");
            case EVENT_STATUS.RegistrationBySchedule:
                return isPrivate.value
                    ? translate("event_status.indicator.published_private")
                    : translate("event_status.indicator.published");
            case EVENT_STATUS.RegistrationOpen:
                return isPrivate.value
                    ? translate("event_status.indicator.registration_open_private")
                    : translate("event_status.indicator.registration_open");
            case EVENT_STATUS.Archived:
                return translate("event_status.indicator.archived");
            default:
                return translate("event_status.indicator.draft");
        }
    });

    const statusButtonText = computed(() => {
        if (isUnderReview.value) {
            return translate("event_status.indicator.btn_change");
        }
        if (!canChangeStatus.value) {
            return translate("event_status.indicator.btn_reactivate");
        }
        if (currentStatus.value === EVENT_STATUS.Draft) {
            return translate("event_status.indicator.btn_publish");
        }
        return translate("event_status.indicator.btn_change");
    });

    const statusTextClass = computed(() => {
        if (isUnderReview.value) {
            return "has-text-warning"; // Yellow - awaiting approval
        }
        switch (currentStatus.value) {
            case EVENT_STATUS.Draft:
                return "has-text-warning"; // Yellow - unfinished, not public yet
            case EVENT_STATUS.RegistrationBySchedule:
                return "has-text-success";
            case EVENT_STATUS.RegistrationOpen:
                return "has-text-success";
            case EVENT_STATUS.Archived:
                return "has-text-danger"; // Closed
            default:
                return "has-text-warning";
        }
    });

    function openStatusModal() {
        eventBus.$emit(EVENT_STATUS_OPEN);
    }
</script>

<style scoped></style>
